import { useQuery } from "@apollo/client";
import _ from "lodash";
import { ReactElement, useCallback } from "react";

import {
  ImportCampaignContactsFromFollowUpMutation,
  ImportCampaignContactsFromFollowUpMutationVariables,
  FollowUpFilterInput,
  CampaignContactFollowUpImportArgs,
  CampaignSortBy,
  ListCampaignsQuery,
  ListCampaignsQueryVariables,
  CampaignListResult,
} from "~frontend/__generated__/graphql";
import Loading from "~frontend/components/Loading";
import {
  FOLLOW_UP_CAMPAIGN_CONTACT_IMPORT,
  LIST_CAMPAIGNS,
} from "~frontend/graphql/campaigns";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import useMutation from "~frontend/util/useMutation";

import { CampaignForEditor } from "../../types";
import ImportForm from "./ImportForm";

export default function FollowUpImport({
  campaign,
  readOnly,
  previousJobArgs,
  displayErrorToast,
  resetContactSourceSettings,
}: {
  campaign: CampaignForEditor;
  readOnly: boolean;
  previousJobArgs?: CampaignContactFollowUpImportArgs;
  displayErrorToast: (message: string, errors: unknown) => void;
  resetContactSourceSettings: (() => void) | null;
}): ReactElement {
  const { subaccount } = useSubaccount()!;

  const [startImport] = useMutation<
    ImportCampaignContactsFromFollowUpMutation,
    ImportCampaignContactsFromFollowUpMutationVariables
  >(FOLLOW_UP_CAMPAIGN_CONTACT_IMPORT);

  const ngpVanCampaignIntegrationEnabled = !!(
    campaign.integration && campaign.integration.service == "NGPVAN"
  );

  const startFollowUpImport = useCallback(
    async ({
      followUpCampaignIds,
      followUpFilter,
      followUpTagIds,
      followUpScriptVariantIds,
    }: {
      followUpCampaignIds: string[];
      followUpFilter: FollowUpFilterInput;
      followUpTagIds: string[] | null;
      followUpScriptVariantIds: string[] | null;
    }) => {
      const { errors } = await startImport({
        variables: {
          input: {
            campaignId: campaign.id,
            followUpCampaignIds,
            followUpFilter,
            followUpTagIds,
            followUpScriptVariantIds,
          },
        },
      });

      if (errors) {
        displayErrorToast("Failed to start follow-up import", errors);
      }
    },
    [campaign, startImport, displayErrorToast]
  );

  const initialValues = {
    campaignId: campaign.id,
    followUpCampaignIds: previousJobArgs?.followUpCampaignIds || [],
    followUpFilter: previousJobArgs?.followUpFilter || FollowUpFilterInput.All,
    followUpTagIds: previousJobArgs?.followUpTagIds || [],
    followUpScriptVariantIds: previousJobArgs?.followUpScriptVariantIds || [],
  };

  const campaignIntegrationFilters = ngpVanCampaignIntegrationEnabled
    ? {
        integrationId: campaign.integration?.id,
        ngpVanDatabaseMode: campaign.integrationSettings?.databaseMode,
      }
    : {};

  const selectedCampaignResults = useQuery<
    ListCampaignsQuery,
    ListCampaignsQueryVariables
  >(LIST_CAMPAIGNS, {
    variables: {
      input: {
        filter: { idIn: initialValues.followUpCampaignIds },
        sort: CampaignSortBy.CreatedAtAsc,
        subaccountId: subaccount.id,
        limit: initialValues.followUpCampaignIds.length,
      },
    },
    fetchPolicy: "network-only",
  });

  if (selectedCampaignResults.loading) {
    return <Loading />;
  }

  const initialCampaignData = selectedCampaignResults.data!.listCampaigns
    .campaigns as CampaignListResult[];

  return (
    <ImportForm
      onSubmit={readOnly ? undefined : startFollowUpImport}
      initialValues={initialValues}
      initialCampaignData={initialCampaignData}
      readOnly={readOnly}
      resetContactSourceSettings={resetContactSourceSettings}
      campaignIntegrationFilters={campaignIntegrationFilters}
    />
  );
}
